import { styled, Typography } from '@mui/material';
import appStore from 'assets/appstore.png';
import googlePlay from 'assets/googleplay.png';
import exosLogo from 'assets/logo.png';
import Container from 'components/Container';
import SignUpQrCode from 'components/SignUpQrCode';
import useMixpanel from 'hooks/useMixpanel';
import React, { useEffect } from 'react';
import {
  APP_STORE_URL,
  getAppStoreUrl,
  getMobilePlatform,
  PLAY_STORE_URL,
} from 'utils/appStore';

const Logo = styled('img')(({ theme }) => ({
  width: '90px',
  marginTop: theme.baseUnit * 6,
  marginBottom: theme.baseUnit * 8,
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  backgroundColor: theme.colors.gray['1'],
  marginTop: theme.baseUnit * 4,
  marginBottom: theme.baseUnit * 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.textVariant.title.xl,
  marginBottom: theme.baseUnit * 8,
  maxWidth: 486,
  textAlign: 'center',
}));

const Text = styled(Typography)(({ theme }) => ({
  ...theme.typography.textVariant.title.l,
  color: theme.colors.alias.textSecondary,
  marginTop: theme.baseUnit * 8,
  marginBottom: theme.baseUnit * 4,
  maxWidth: 514,
  textAlign: 'center',
}));

const StoreLinks = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.baseUnit * 3,
  marginBottom: theme.baseUnit * 8,
}));

const StoreButton = styled('img')({ width: '178px', height: 'auto' });

interface DownloadAppDeps {
  userAgent: string;
  redirect: (url: string) => void;
}

const defaultRedirect = (url: string) => window.location.replace(url);

export const getDefaultDeps = (): DownloadAppDeps => ({
  userAgent: navigator.userAgent,
  redirect: defaultRedirect,
});

interface DownloadAppProps {
  deps?: DownloadAppDeps;
}

// Public landing page for the sign-up QR code: phones go straight to their store,
// everything else gets the store badges and the QR code to scan.
const DownloadApp: React.FC<DownloadAppProps> = ({
  deps = getDefaultDeps(),
}) => {
  const { userAgent, redirect } = deps;
  const track = useMixpanel();
  const storeUrl = getAppStoreUrl(userAgent);

  useEffect(() => {
    track('Screen View', {
      screen_name: 'download_app',
      platform: getMobilePlatform(userAgent) ?? 'web',
    });
  }, [track, userAgent]);

  useEffect(() => {
    if (storeUrl) {
      redirect(storeUrl);
    }
  }, [storeUrl, redirect]);

  return (
    <main>
      <StyledContainer>
        <Logo src={exosLogo} alt="Exos" />
        <Title variant="h1">Get the Exos app</Title>
        <StoreLinks>
          <a href={APP_STORE_URL}>
            <StoreButton src={appStore} alt="Download on the App Store" />
          </a>
          <a href={PLAY_STORE_URL}>
            <StoreButton src={googlePlay} alt="Get it on Google Play" />
          </a>
        </StoreLinks>
        <SignUpQrCode size={256} />
        <Text>
          Scan the QR code with your phone to download the app and register your
          account.
        </Text>
      </StyledContainer>
    </main>
  );
};

export default DownloadApp;
